import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PresignedImage from '../../../components/common/PresignedImage';
import FileTypeIcon from '../../../components/common/FileTypeIcon';
import { formatFileSize, isDisplayableImage } from '../../../utils/fileUtils';

const stripPreviewText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = String(html).replace(/<br\s*\/?>/gi, '\n');
  return (tmp.textContent || tmp.innerText || '').trim();
};

const SaveToVaultModal = ({ open, defaultTitle, message, onClose, onSave, saving }) => {
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle || '');
  }, [open, defaultTitle]);

  if (!open || typeof document === 'undefined') return null;

  const canSave = Boolean(title.trim()) && !saving;
  const chatText = stripPreviewText(message?.content);
  const attachments = (message?.attachments || []).filter(
    (item) => item?.fileUrl && String(item.fileType || '').toUpperCase() !== 'LINK',
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-gray-950/80"
      role="dialog"
      aria-modal="true"
      aria-label="Save to Vault"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Save to Vault</h2>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          This document will be available in every chat. Give it a title so you can find it later.
        </p>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canSave) onSave(title.trim());
          }}
          placeholder="Document title"
          maxLength={80}
          autoFocus
          className="mt-4 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
        />
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Chat</p>
          {chatText ? (
            <div
              className="mt-1.5 max-h-40 overflow-y-auto text-sm text-gray-800 dark:text-gray-100 vault-preview-html"
              dangerouslySetInnerHTML={{ __html: message.content }}
            />
          ) : (
            <p className="mt-1.5 text-xs text-gray-400">No chat text</p>
          )}
          {attachments.length > 0 ? (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Attachments</p>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {attachments.map((item) => (
                  <div
                    key={item.id || item.fileUrl}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
                  >
                    {isDisplayableImage(item) ? (
                      <PresignedImage
                        fileKey={item.fileUrl}
                        alt={item.fileName || ''}
                        className="w-full h-20 object-cover"
                      />
                    ) : (
                      <div className="flex items-center gap-2 px-2 py-2">
                        <FileTypeIcon fileName={item.fileName} size="sm" />
                        <div className="min-w-0">
                          <p className="text-[11px] font-medium text-gray-800 dark:text-gray-100 truncate">
                            {item.fileName || 'file'}
                          </p>
                          <p className="text-[10px] text-gray-400">{formatFileSize(item.fileSize)}</p>
                        </div>
                      </div>
                    )}
                    {isDisplayableImage(item) ? (
                      <p className="px-2 py-1 text-[10px] text-gray-500 truncate">{item.fileName}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <style>{`
          .vault-preview-html img { max-height: 80px; border-radius: 8px; }
          .vault-preview-html p { margin: 0 0 0.4em; }
        `}</style>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSave(title.trim())}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SaveToVaultModal;
